//
//
//
//
//
//
//

import { ARTICLE_TYPE } from '~/static/constants.js';
import analyticEvents from '~/services/analyticEvents';
const ArticleSlug = () => import('~/components/Articles/ArticleSlug.vue');
const Error404 = () => import('~/components/Error/Page404.vue');

const CDN = process.env.CDN_ENDPOINT;

export default {
    name: 'ArticleSlugWrapper',
    components: { ArticleSlug, Error404 },
    inject: ['blogTopic'],
    data() {
        return {
            guideTabs: [],
            articleMetadata: null,
            isGuideArticle: true
        };
    },
    async fetch() {
        try {
            const slug = this.$route.path.split('/').pop();
            const guideTabsSlugsKey = `${this.blogTopic}_guide_tabs_slugs`;

            // Try to get guide tabs (cache or API)
            try {
                this.guideTabs = this.$nuxt.context[guideTabsSlugsKey] || [];

                if (!this.guideTabs.length) {
                    const {
                        data: { data: tabs }
                    } = await this.$api.guides.getGuideTabs(this.blogTopic);

                    this.guideTabs = tabs;
                    this.$nuxt.context[guideTabsSlugsKey] = this.guideTabs;
                }
            } catch (error) {
                // Continue silently if guide tabs fail
                this.guideTabs = [];
            }

            const guideTabsSlugs = this.formatGuideTabsSlugs(this.guideTabs);

            if (guideTabsSlugs.includes(slug)) {
                await this.fetchAndFormatGuideData(this.blogTopic, slug);
            } else {
                this.isGuideArticle = false;

                const {
                    data: { data, state }
                } = await this.$api.articles.getArticle(this.blogTopic, slug);

                if (state) {
                    this.formatBlogData(data);
                } else {
                    throw this.$nuxt.error({ statusCode: 404 });
                }
            }
        } catch (err) {
            throw this.$nuxt.error({ statusCode: 404 });
        }
    },

    updated() {
        if (this.articleMetadata && !this.isGuideArticle) {
            const trackingEvent = {
                event: analyticEvents.blog_opened,
                data: {
                    exam_name: this.blogTopic.toUpperCase(),
                    blog_url: `${window.location.origin}${this.$route.path}`
                }
            };
            this.$store.dispatch('analytics/trackEvent', trackingEvent);
        }
    },
    methods: {
        formatGuideTabsSlugs(guideTabs) {
            return guideTabs.map((tab) => tab.slug);
        },
        async fetchAndFormatGuideData(guideSlug, tabSlug) {
            const guideContentKey = `${guideSlug}_${tabSlug}_guide_content`;
            let articleContent = this.$nuxt.context[guideContentKey];

            if (!articleContent) {
                const {
                    data: { data: guideData }
                } = await this.$api.guides.getGuideData(guideSlug, tabSlug);
                articleContent = guideData;

                this.$nuxt.context[guideContentKey] = guideData;
            }
            this.formatGuideData(articleContent);
        },
        splitArticleContentAndIntroduction(article) {
            // Split content at the first <h2>
            const h2Regex = /<h2.*?>.*?<\/h2>/i;
            const splitIndex = article.search(h2Regex);

            let articleIntroduction = '';
            let articleContent = '';

            if (splitIndex !== -1) {
                articleIntroduction = article.slice(0, splitIndex);
                articleContent = article.slice(splitIndex);
            } else {
                articleContent = article;
            }
            return { articleIntroduction, articleContent };
        },
        formatBlogData(data) {
            const article = data.article;
            const relatedArticles = data.related_article;

            const blogData = {
                type: ARTICLE_TYPE.BLOG,
                slug: null,
                topic: null,
                tableOfContentList: null,
                articleIntroduction: null,
                articleContent: null,
                faqSchemaStructure: null,
                articleSchemaStructure: null,
                metaTitle: null,
                metaDescription: null,
                // Specific information for Blog Cards.
                metaInformation: null,
                faqHeadline: null,
                faqs: null,
                relatedArticles: null
            };

            // SEAL
            Object.seal(blogData);

            blogData.slug = article.slug;
            blogData.topic = article.data.title;
            blogData.tableOfContentList = this.setupTableOfContent(
                article.site_data.table_of_contents
            );
            const { articleContent, articleIntroduction } = this.splitArticleContentAndIntroduction(
                article.content
            );
            blogData.articleContent = articleContent;
            blogData.articleIntroduction = articleIntroduction;

            blogData.faqSchemaStructure = article.site_data.faq_schema;
            blogData.articleSchemaStructure = article.site_data.article_schema;
            blogData.metaTitle = article.data.title;
            blogData.metaDescription = article.data.metadesc;
            blogData.metaInformation = {
                title: article.data.title,
                subTitle: article.data.metadesc,
                duration: article.data.read_time + 'mins',
                timestamp: article.updated_at,
                imageUrl: article.data.image_url
            };
            blogData.faqHeadline = article.site_data.faq_headline;
            blogData.faqs = article.site_data.faqs;
            blogData.relatedArticles = relatedArticles;

            this.articleMetadata = blogData;

            if (blogData.faqHeadline) {
                blogData.tableOfContentList.push(blogData.faqHeadline);
            }
        },
        formatGuideData(data) {
            if (!data) return;
            const article = data.tab_details;

            const guideData = {
                type: ARTICLE_TYPE.GUIDE,
                slug: null,
                topic: null,
                tableOfContentList: null,
                articleIntroduction: null,
                articleContent: null,
                faqSchemaStructure: null,
                articleSchemaStructure: null,
                metaTitle: null,
                metaDescription: null,
                // Specific information for Blog Cards.
                metaInformation: null,
                faqHeadline: null,
                faqs: [],
                guideTopics: []
            };

            // SEAL
            Object.seal(guideData);

            guideData.slug = article.slug;
            guideData.topic = article.title;
            guideData.tableOfContentList = this.setupTableOfContent(article.sections);
            const fullArticle = `<h1><strong>${article.heading}</strong></h1>
            ${article.content}`;
            const { articleContent, articleIntroduction } =
                this.splitArticleContentAndIntroduction(fullArticle);
            guideData.articleContent = articleContent;
            guideData.articleIntroduction = articleIntroduction;

            if (article.faqs.length) {
                guideData.faqSchemaStructure = this.formatFaqSchema(article.faqs);
            }

            guideData.articleSchemaStructure = this.formatArticleSchema(article);
            guideData.metaTitle = article.title;
            guideData.metaDescription = article.meta_description;
            guideData.metaInformation = {
                title: article.title,
                subTitle: article.meta_description,
                imageUrl: data.hero_image_url
            };
            guideData.faqHeadline = article.faq_headline;

            guideData.faqs = article.faqs || [];

            this.articleMetadata = guideData;

            if (guideData.faqs.length && guideData.faqHeadline) {
                guideData.tableOfContentList.push(guideData.faqHeadline);
            }

            guideData.guideTopics = this.guideTabs.map((tab) => ({
                id: tab.order,
                topic: tab.name,
                redirection: `/${
                    tab.slug === this.blogTopic ? tab.slug : `${this.blogTopic}/${tab.slug}`
                }`,
                title: tab.name
            }));
        },

        setupTableOfContent(data) {
            return data.map((ele) => ele?.text || ele?.title);
        },
        formatFaqSchema(faqs) {
            return {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq?.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq?.answer
                    }
                }))
            };
        },
        formatArticleSchema(article) {
            return {
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': 'https://prep.yocket.com/gre'
                },
                headline: article?.title,
                description: article?.meta_description,
                image: '',
                author: {
                    '@type': 'Organization',
                    name: 'Yocket Prep',
                    url: 'https://prep.yocket.com/'
                },
                publisher: {
                    '@type': 'Organization',
                    name: 'Yocket Prep',
                    logo: {
                        '@type': 'ImageObject',
                        url: `${CDN}/images/yocket_prep_logo.png`
                    }
                }
            };
        }
    }
};
